/**
 * Measurement system a user views sizes and weights in.
 *
 * Everything the API returns and accepts is imperial - inches, pounds, square inches, cubic feet -
 * and the pricing engine only ever sees those values. This selects how they are rendered, so
 * switching it can never change a price.
 */
export enum MeasurementUnitEnum {
  IMPERIAL = 'IMPERIAL',
  METRIC = 'METRIC',
}

/**
 * A physical quantity, the imperial unit it is stored in, and the metric unit it is shown in.
 *
 * The metric unit is the one the print trade actually uses for that quantity. Caliper is microns
 * rather than millimetres on purpose: at two decimals a 0.0075" and a 0.0078" stock both render as
 * 0.19mm and become indistinguishable in the paper picker.
 */
export enum MeasureType {
  /** Trim, flat and sheet dimensions. inches -> millimetres. */
  LENGTH = 'LENGTH',
  /** Stock thickness. inches -> microns. */
  CALIPER = 'CALIPER',
  /** Weights. pounds -> kilograms. */
  WEIGHT = 'WEIGHT',
  /** Areas. square inches -> square centimetres. */
  AREA = 'AREA',
  /** Volumes. cubic feet -> cubic metres. */
  VOLUME = 'VOLUME',
}

export interface MeasureSpec {
  /** Multiply an imperial value by this to get the metric one. Exact by definition. */
  factor: number;
  /** Tight form that sits directly after a number, e.g. `8.5"`. */
  imperialSymbol: string;
  metricSymbol: string;
  /**
   * Spelled form for a label or column header, e.g. `Width (in)`.
   *
   * Only length and caliper differ from the symbol: `Width (")` reads badly where `8.5"` reads
   * fine, so headers spell the inch out.
   */
  imperialAbbrev: string;
  metricAbbrev: string;
}

export const MEASURE_SPECS: Record<MeasureType, MeasureSpec> = {
  [MeasureType.LENGTH]: {
    factor: 25.4,
    imperialSymbol: '"',
    metricSymbol: 'mm',
    imperialAbbrev: 'in',
    metricAbbrev: 'mm',
  },
  [MeasureType.CALIPER]: {
    factor: 25400,
    imperialSymbol: '"',
    metricSymbol: 'µm',
    imperialAbbrev: 'in',
    metricAbbrev: 'µm',
  },
  [MeasureType.WEIGHT]: {
    factor: 0.45359237,
    imperialSymbol: 'lb',
    metricSymbol: 'kg',
    imperialAbbrev: 'lb',
    metricAbbrev: 'kg',
  },
  [MeasureType.AREA]: {
    factor: 6.4516,
    imperialSymbol: 'sq in',
    metricSymbol: 'cm²',
    imperialAbbrev: 'sq in',
    metricAbbrev: 'cm²',
  },
  [MeasureType.VOLUME]: {
    factor: 0.028316846592,
    imperialSymbol: 'cu ft',
    metricSymbol: 'm³',
    imperialAbbrev: 'cu ft',
    metricAbbrev: 'm³',
  },
};

/**
 * What a supplier-entered value represents, if anything.
 *
 * Service options are free text - a "HOLE DIAMETER" of `.25` and a "HOLE NUMBER" of `3` are both
 * just strings - so nothing in the data says which is a measurement. This tags the ones that are;
 * `NONE` (the default) means show the value exactly as entered.
 */
export enum MeasurementQuantityEnum {
  NONE = 'NONE',
  LENGTH = 'LENGTH',
  CALIPER = 'CALIPER',
  WEIGHT = 'WEIGHT',
  AREA = 'AREA',
  VOLUME = 'VOLUME',
}

/** The MeasureType a tag renders with, or `undefined` when it is not a measurement. */
export const measureTypeForQuantity = (
  uom?: MeasurementQuantityEnum | null
): MeasureType | undefined => {
  switch (uom) {
    case MeasurementQuantityEnum.LENGTH:
      return MeasureType.LENGTH;
    case MeasurementQuantityEnum.CALIPER:
      return MeasureType.CALIPER;
    case MeasurementQuantityEnum.WEIGHT:
      return MeasureType.WEIGHT;
    case MeasurementQuantityEnum.AREA:
      return MeasureType.AREA;
    case MeasurementQuantityEnum.VOLUME:
      return MeasureType.VOLUME;
    default:
      return undefined;
  }
};

/** Decimal places a converted metric value is presented at. */
export const METRIC_DISPLAY_DECIMALS = 3;

/**
 * Decimals a value converted *into* the base system keeps.
 *
 * The same six the API stores, and for the same reason: a millimetre value entered to three
 * decimals only comes back exactly if the inches it becomes keep six. At two, switching a form to
 * imperial and back turned an entered 888 into 887.98 - 888 / 25.4 shown as 34.96, and 34.96 x
 * 25.4 is 887.984.
 */
export const BASE_DECIMALS = 6;

/**
 * Decimals a person may type into a length field.
 *
 * The size forms enforce this (`RULE_MEASURE_PRECISION(3, LENGTH)`), which makes it a reliable
 * tell: a stored length carrying more than this was not typed, it was converted from a metric
 * entry and kept to {@link BASE_DECIMALS}. That is the difference between a supplier's 8.125"
 * and an A4's 8.267717", and the two want showing differently.
 */
export const TYPED_LENGTH_DECIMALS = 3;

/**
 * The loosest typed precision of any quantity - caliper, at five.
 *
 * For the places that round a measurement without knowing which quantity it is. Anything longer
 * than this is derived whatever it measures, so rounding it loses nothing a person entered.
 */
export const MAX_TYPED_DECIMALS = 5;

/**
 * Decimals a *derived* imperial value is read to.
 *
 * Only ever applied to a value that was converted rather than typed. A hundredth of an inch is
 * a quarter of a millimetre - finer than anything is produced to - so an A4 reads 8.27x11.69
 * rather than 8.267717x11.692913.
 */
export const DERIVED_IMPERIAL_DECIMALS = 2;

/**
 * The same for a derived value below one whole inch.
 *
 * Two would round a converted 0.125mm bleed to 0.00 and lose it altogether. Below one, the
 * digits after the point are the whole number.
 */
export const DERIVED_SMALL_IMPERIAL_DECIMALS = 3;

/**
 * Query parameter the API reads so server-rendered output - quotes, job PDFs, Excel exports -
 * matches the UI, including a live switch.
 *
 * This replaced an `x-measurement-unit` header. A header is invisible in the endpoint's contract
 * and is dropped by anything that rebuilds the request, and the API then falls back to imperial
 * without failing, so a metric user is quietly served inches. Writes state the unit their numbers
 * were typed in separately, as `enteredUnit` in the body.
 */
export const MEASUREMENT_UNIT_PARAM = 'measurementUnit';

export const BASIS_WEIGHT_IMPERIAL_ABBREV = 'lb';
export const BASIS_WEIGHT_METRIC_ABBREV = 'gsm';
